import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getLocalItem, removeLocalItem, setLocalItem } from '../utils/safeStorage';
import { calcNights } from '../utils/bookingUtils';

const storage = {
  getItem: (name) => getLocalItem(name) || null,
  setItem: (name, value) => setLocalItem(name, value),
  removeItem: (name) => removeLocalItem(name),
};

const emptyGuestDetails = {
  fullName: '',
  email: '',
  phone: '',
  arrivalTime: '',
  specialRequests: '',
};

const initialState = {
  stayId: '',
  stayData: null,
  checkIn: '',
  checkOut: '',
  nights: 0,
  guests: 0,
  roomTypeId: '',
  roomCount: 1,
  mealPlanId: '',
  quoteId: '',
  ratePlanCode: '',
  selectedAddons: [],
  cardOfferId: '',
  promoCode: '',
  appliedCoupon: null,
  pricingBreakdown: null,
  paymentOptionSelected: 'full',
  termsAccepted: false,
  bookingId: '',
  guestDetails: emptyGuestDetails,
};

export const selectStayId = (state) => state.stayId;
export const selectStayData = (state) => state.stayData;
export const selectCheckIn = (state) => state.checkIn;
export const selectCheckOut = (state) => state.checkOut;
export const selectNights = (state) => state.nights;
export const selectGuests = (state) => state.guests;
export const selectRoomTypeId = (state) => state.roomTypeId;
export const selectRoomCount = (state) => state.roomCount;
export const selectMealPlanId = (state) => state.mealPlanId;
export const selectQuoteId = (state) => state.quoteId;
export const selectRatePlanCode = (state) => state.ratePlanCode;
export const selectSelectedAddons = (state) => state.selectedAddons;
export const selectCardOfferId = (state) => state.cardOfferId;
export const selectPromoCode = (state) => state.promoCode;
export const selectAppliedCoupon = (state) => state.appliedCoupon;
export const selectPricingBreakdown = (state) => state.pricingBreakdown;
export const selectPaymentOptionSelected = (state) => state.paymentOptionSelected;
export const selectTermsAccepted = (state) => state.termsAccepted;
export const selectBookingId = (state) => state.bookingId;
export const selectGuestDetails = (state) => state.guestDetails;
export const selectSetSelection = (state) => state.setSelection;
export const selectSetStayData = (state) => state.setStayData;
export const selectSetDates = (state) => state.setDates;
export const selectSetGuests = (state) => state.setGuests;
export const selectSetRoomType = (state) => state.setRoomType;
export const selectSetMealPlanId = (state) => state.setMealPlanId;
export const selectToggleAddon = (state) => state.toggleAddon;
export const selectSetSelectedAddons = (state) => state.setSelectedAddons;
export const selectSetCardOfferId = (state) => state.setCardOfferId;
export const selectSetPromoCode = (state) => state.setPromoCode;
export const selectSetAppliedCoupon = (state) => state.setAppliedCoupon;
export const selectSetPricingBreakdown = (state) => state.setPricingBreakdown;
export const selectSetPaymentOptionSelected = (state) => state.setPaymentOptionSelected;
export const selectSetTermsAccepted = (state) => state.setTermsAccepted;
export const selectSetBookingId = (state) => state.setBookingId;
export const selectSetGuestDetails = (state) => state.setGuestDetails;
export const selectResetBooking = (state) => state.resetBooking;

const sameCoupon = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return String(a.id || '') === String(b.id || '') && String(a.code || '') === String(b.code || '');
};

const sameAddons = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((item, idx) => {
    const other = b[idx];
    return (
      String(item?.id || '') === String(other?.id || '') &&
      Number(item?.price ?? item?.amount ?? 0) === Number(other?.price ?? other?.amount ?? 0)
    );
  });
};

const samePricing = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const aPaymentPartial = a.paymentOptions?.partial;
  const bPaymentPartial = b.paymentOptions?.partial;
  const aBreakdown = JSON.stringify(a.taxFeeBreakdown || null);
  const bBreakdown = JSON.stringify(b.taxFeeBreakdown || null);
  return (
    Number(a.base || 0) === Number(b.base || 0) &&
    Number(a.addons || 0) === Number(b.addons || 0) &&
    Number(a.fees || 0) === Number(b.fees || 0) &&
    Number(a.taxes || 0) === Number(b.taxes || 0) &&
    Number(a.discount || 0) === Number(b.discount || 0) &&
    Number(a.total || 0) === Number(b.total || 0) &&
    Number(a.nights || 0) === Number(b.nights || 0) &&
    String(a.currency || '') === String(b.currency || '') &&
    Boolean(aPaymentPartial?.isAvailable) === Boolean(bPaymentPartial?.isAvailable) &&
    Number(aPaymentPartial?.percentage || 0) === Number(bPaymentPartial?.percentage || 0) &&
    aBreakdown === bBreakdown
  );
};

const policySignature = (data) => {
  if (!data) return '';
  const payload = {
    house_rules_sections: data.house_rules_sections ?? null,
    house_rules: data.house_rules ?? null,
    pet_policy: data.pet_policy ?? null,
    smoking_policy: data.smoking_policy ?? null,
    id_requirement: data.id_requirement ?? null,
    guest_profile: data.guest_profile ?? null,
    cancellation_policy: data.cancellation_policy ?? null,
    refund_rules: data.refund_rules ?? data.refund_policy ?? null,
    security_deposit: data.security_deposit ?? null,
    security_deposit_policy: data.security_deposit_policy ?? null,
  };
  try {
    return JSON.stringify(payload);
  } catch (_) {
    return '';
  }
};

const sameStayPolicies = (a, b) => policySignature(a) === policySignature(b);

const sameStay = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    String(a.id || '') === String(b.id || '') &&
    String(a.slug || '') === String(b.slug || '') &&
    String(a.title || '') === String(b.title || '') &&
    String(a.city || '') === String(b.city || '') &&
    String(a.area || '') === String(b.area || '') &&
    String(a.coverImage || '') === String(b.coverImage || '')
  );
};

const sameGuestDetails = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    String(a.fullName || '') === String(b.fullName || '') &&
    String(a.email || '') === String(b.email || '') &&
    String(a.phone || '') === String(b.phone || '') &&
    String(a.arrivalTime || '') === String(b.arrivalTime || '') &&
    String(a.specialRequests || '') === String(b.specialRequests || '')
  );
};

const partialize = (state) => ({
  stayId: state.stayId,
  checkIn: state.checkIn,
  checkOut: state.checkOut,
  guests: state.guests,
  roomTypeId: state.roomTypeId,
  roomCount: state.roomCount,
  mealPlanId: state.mealPlanId,
  quoteId: state.quoteId,
  ratePlanCode: state.ratePlanCode,
  selectedAddons: state.selectedAddons,
  cardOfferId: state.cardOfferId,
  promoCode: state.promoCode,
  appliedCoupon: state.appliedCoupon,
  paymentOptionSelected: state.paymentOptionSelected,
  termsAccepted: state.termsAccepted,
  bookingId: state.bookingId,
  guestDetails: state.guestDetails,
});

const useBookingStore = create(
  persist(
    (set, get) => ({
      ...initialState,
      setSelection: (payload = {}) => {
        const state = get();
        const nextCheckIn = payload.checkIn ?? state.checkIn;
        const nextCheckOut = payload.checkOut ?? state.checkOut;
        const nextGuests = payload.guests ?? state.guests;
        const nextRoomTypeId = payload.roomTypeId ?? state.roomTypeId;
        const nextRoomCount = Math.max(1, Number(payload.roomCount ?? state.roomCount) || 1);
        const nextMealPlanId = payload.mealPlanId ?? state.mealPlanId;
        const nextQuoteId = payload.quoteId ?? state.quoteId;
        const nextRatePlanCode = payload.ratePlanCode ?? state.ratePlanCode;
        const nextStayId = payload.stayId ?? state.stayId;
        const nextPromoCode = payload.promoCode ?? state.promoCode;
        const nextApplied = payload.appliedCoupon ?? state.appliedCoupon;
        const nextCardOfferId = payload.cardOfferId ?? state.cardOfferId;
        const nextPaymentOptionSelected = payload.paymentOptionSelected ?? state.paymentOptionSelected;
        const nextTerms = payload.termsAccepted ?? state.termsAccepted;
        const nextNights = calcNights(nextCheckIn, nextCheckOut);

        const changed =
          nextStayId !== state.stayId ||
          nextCheckIn !== state.checkIn ||
          nextCheckOut !== state.checkOut ||
          Number(nextGuests) !== Number(state.guests) ||
          String(nextRoomTypeId || '') !== String(state.roomTypeId || '') ||
          Number(nextRoomCount) !== Number(state.roomCount || 1) ||
          String(nextMealPlanId || '') !== String(state.mealPlanId || '') ||
          String(nextQuoteId || '') !== String(state.quoteId || '') ||
          String(nextRatePlanCode || '') !== String(state.ratePlanCode || '') ||
          nextPromoCode !== state.promoCode ||
          nextCardOfferId !== state.cardOfferId ||
          nextPaymentOptionSelected !== state.paymentOptionSelected ||
          nextTerms !== state.termsAccepted ||
          nextNights !== state.nights ||
          !sameCoupon(nextApplied, state.appliedCoupon);

        if (!changed) return;
        set({
          ...payload,
          stayId: nextStayId,
          checkIn: nextCheckIn,
          checkOut: nextCheckOut,
          guests: nextGuests,
          roomTypeId: nextRoomTypeId,
          roomCount: nextRoomCount,
          mealPlanId: nextMealPlanId,
          quoteId: nextQuoteId,
          ratePlanCode: nextRatePlanCode,
          promoCode: nextPromoCode,
          cardOfferId: nextCardOfferId,
          paymentOptionSelected: nextPaymentOptionSelected,
          termsAccepted: nextTerms,
          appliedCoupon: nextApplied,
          nights: nextNights,
        });
      },
      setStayData: (stayData) =>
        set((state) => {
          const same = sameStay(stayData, state.stayData);
          if (same && sameStayPolicies(stayData, state.stayData)) return state;
          const merged = same ? { ...(state.stayData || {}), ...(stayData || {}) } : stayData;
          return {
            stayData: merged,
            stayId: stayData?.id ?? state.stayId,
          };
        }),
      setDates: (checkIn, checkOut) =>
        set((state) => {
          const nextNights = calcNights(checkIn, checkOut);
          if (state.checkIn === checkIn && state.checkOut === checkOut && state.nights === nextNights) return state;
          return { checkIn, checkOut, nights: nextNights };
        }),
      setGuests: (guests) =>
        set((state) => {
          const nextGuests = Math.max(0, Number(guests || 0) || 0);
          if (Number(state.guests) === nextGuests) return state;
          return { guests: nextGuests };
        }),
      setRoomType: (roomTypeId, roomCount = 1) =>
        set((state) => {
          const nextRoomTypeId = roomTypeId || '';
          const nextRoomCount = Math.max(1, Number(roomCount || 1) || 1);
          if (
            String(state.roomTypeId || '') === String(nextRoomTypeId || '') &&
            Number(state.roomCount || 1) === Number(nextRoomCount)
          ) {
            return state;
          }
          return { roomTypeId: nextRoomTypeId, roomCount: nextRoomCount };
        }),
      setMealPlanId: (mealPlanId) =>
        set((state) => {
          const nextMealPlanId = mealPlanId || '';
          if (String(state.mealPlanId || '') === String(nextMealPlanId || '')) return state;
          return { mealPlanId: nextMealPlanId };
        }),
      toggleAddon: (addon) =>
        set((state) => {
          const exists = state.selectedAddons.some((item) => item.id === addon.id);
          const selectedAddons = exists
            ? state.selectedAddons.filter((item) => item.id !== addon.id)
            : [...state.selectedAddons, addon];
          if (sameAddons(selectedAddons, state.selectedAddons)) return state;
          return { selectedAddons };
        }),
      setSelectedAddons: (selectedAddons = []) =>
        set((state) => {
          if (sameAddons(selectedAddons, state.selectedAddons)) return state;
          return { selectedAddons };
        }),
      setCardOfferId: (cardOfferId) =>
        set((state) => (state.cardOfferId === cardOfferId ? state : { cardOfferId })),
      setPromoCode: (promoCode) =>
        set((state) => (state.promoCode === promoCode ? state : { promoCode })),
      setAppliedCoupon: (appliedCoupon) =>
        set((state) => (sameCoupon(appliedCoupon, state.appliedCoupon) ? state : { appliedCoupon })),
      setPricingBreakdown: (pricingBreakdown) =>
        set((state) => {
          const nextPaymentOptionSelected =
            pricingBreakdown?.paymentOptions?.partial?.isAvailable
              ? state.paymentOptionSelected
              : 'full';
          if (
            samePricing(pricingBreakdown, state.pricingBreakdown) &&
            nextPaymentOptionSelected === state.paymentOptionSelected
          ) {
            return state;
          }
          return {
            pricingBreakdown,
            paymentOptionSelected: nextPaymentOptionSelected,
          };
        }),
      setPaymentOptionSelected: (paymentOptionSelected) =>
        set((state) => {
          const nextValue = paymentOptionSelected === 'partial' ? 'partial' : 'full';
          if (nextValue === 'partial' && !state.pricingBreakdown?.paymentOptions?.partial?.isAvailable) {
            return state;
          }
          return state.paymentOptionSelected === nextValue ? state : { paymentOptionSelected: nextValue };
        }),
      setTermsAccepted: (termsAccepted) =>
        set((state) => (state.termsAccepted === termsAccepted ? state : { termsAccepted })),
      setBookingId: (bookingId) =>
        set((state) => (state.bookingId === bookingId ? state : { bookingId })),
      setGuestDetails: (patch) =>
        set((state) => {
          const nextGuestDetails = {
            ...state.guestDetails,
            ...(patch || {}),
          };
          if (sameGuestDetails(nextGuestDetails, state.guestDetails)) return state;
          return { guestDetails: nextGuestDetails };
        }),
      resetBooking: () => set({ ...initialState }),
    }),
    {
      name: 'homevilas.booking',
      storage: createJSONStorage(() => storage),
      partialize,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const nextNights = calcNights(state.checkIn, state.checkOut);
        if (state.nights !== nextNights && typeof state.setDates === 'function') {
          state.setDates(state.checkIn, state.checkOut);
        }
      },
    }
  )
);

export default useBookingStore;
